import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  BatchConfigDTO,
  BatchJobDTO,
  BatchPreviewDTO,
  BatchResultDTO,
} from '../types';

interface BatchTransactionState {
  // Config management
  configs: BatchConfigDTO[];
  configsLoading: boolean;
  selectedConfig: BatchConfigDTO | null;

  // Wizard state
  wizardStep: number;
  activeJobId: number | null;
  uploadedFileName: string | null;
  previewData: BatchPreviewDTO | null;
  submitResult: BatchResultDTO | null;
  isSubmitting: boolean;

  // Job history
  jobs: BatchJobDTO[];
  jobsLoading: boolean;

  // Actions
  setConfigs: (configs: BatchConfigDTO[]) => void;
  setConfigsLoading: (loading: boolean) => void;
  setSelectedConfig: (config: BatchConfigDTO | null) => void;
  setWizardStep: (step: number) => void;
  setActiveJobId: (id: number | null) => void;
  setUploadedFileName: (name: string | null) => void;
  setPreviewData: (data: BatchPreviewDTO | null) => void;
  setSubmitResult: (result: BatchResultDTO | null) => void;
  setIsSubmitting: (v: boolean) => void;
  setJobs: (jobs: BatchJobDTO[]) => void;
  setJobsLoading: (loading: boolean) => void;
  resetWizard: () => void;
  reset: () => void;
}

const initialState = {
  configs: [],
  configsLoading: false,
  selectedConfig: null,
  wizardStep: 0,
  activeJobId: null,
  uploadedFileName: null,
  previewData: null,
  submitResult: null,
  isSubmitting: false,
  jobs: [],
  jobsLoading: false,
};

export const useBatchTransactionStore = create<BatchTransactionState>()(
  devtools(
    (set) => ({
      ...initialState,

      setConfigs: (configs) => set({ configs }),
      setConfigsLoading: (configsLoading) => set({ configsLoading }),
      setSelectedConfig: (selectedConfig) => set({ selectedConfig }),
      setWizardStep: (wizardStep) => set({ wizardStep }),
      setActiveJobId: (activeJobId) => set({ activeJobId }),
      setUploadedFileName: (uploadedFileName) => set({ uploadedFileName }),
      setPreviewData: (previewData) => set({ previewData }),
      setSubmitResult: (submitResult) => set({ submitResult }),
      setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
      setJobs: (jobs) => set({ jobs }),
      setJobsLoading: (jobsLoading) => set({ jobsLoading }),

      resetWizard: () =>
        set({
          wizardStep: 0,
          activeJobId: null,
          uploadedFileName: null,
          previewData: null,
          submitResult: null,
          isSubmitting: false,
        }),

      reset: () => set(initialState),
    }),
    { name: 'batch-transaction-store' }
  )
);
